/**
 * Consumer hook: overlays in-flight optimistic connection operations onto query data.
 *
 * A component reads its own query data (`base`, pristine server truth) and overlays every
 * in-flight operation targeting the same owner/connection, gathered from TanStack Query's
 * mutation cache via `useMutationState` — no event bus, no broadcast; consumers of the same
 * connection independently select the same pending operations by `mutationKey`.
 *
 * Settle via `invalidateQueries` + refetch (or a live-update channel) rather than
 * `setQueryData(responseBody)`, so an out-of-order response can't fold a stale value in.
 */
import {useMutationState} from '@github-ui/react-query'
import {useMemo} from 'react'

import {
  type ConnectionOperation,
  type PendingConnectionOperation,
  applyOperations,
  connectionPathKey,
} from './connection-operations'

/** The shared root segment of every connection mutation key; `useMergedConnection`
 *  subscribes to this broad prefix (see the `useMutationState` call). */
export const CONNECTION_MUTATION_KEY_ROOT = 'connection'

/**
 * The stable, query-agnostic mutation key for a connection. Producers and consumers must
 * agree on this so pending operations can be gathered. The path stays a nested array (not
 * joined) so `['a','b']` and `['a.b']` produce distinct keys; TanStack hashes it structurally.
 */
export function connectionMutationKey(ownerId: string, connectionPath: string[]): unknown[] {
  return [CONNECTION_MUTATION_KEY_ROOT, ownerId, connectionPath]
}

/** The `useMutation` variables shape a connection mutation stores and this hook reads back. */
export type ConnectionMutationVariables = {operation: ConnectionOperation}

/** A pending operation read from the mutation cache, carrying the owner and path from its
 *  own key so a stale `useMutationState` snapshot can be filtered out synchronously. A
 *  superset of `PendingConnectionOperation`, so it feeds `applyOperations` directly. */
type PendingEntry = PendingConnectionOperation & {connectionPath: string[]}

export type ConnectionMutationTarget = {ownerId: string; connectionPath: string[]}
export type SetFieldsOperation = Extract<ConnectionOperation, {type: 'setFields'}>
export type SetFieldsMapper = (fields: Readonly<Record<string, unknown>>) => Record<string, unknown> | undefined

function connectionTargetKey(ownerId: string, connectionPath: string[]): string {
  return JSON.stringify([ownerId, connectionPath])
}

/**
 * Overlays the still-pending operations for `{ownerId, connectionPath}` onto `base`. When
 * nothing is pending, returns `base` unchanged (stable identity → no needless re-render).
 */
export function useMergedConnection<T>({
  base,
  ownerId,
  connectionPath,
  enabled = true,
  mapSetFields,
  identityPath,
}: {
  base: T
  ownerId: string
  connectionPath: string[]
  /** When false, skip the overlay and return `base` unchanged. */
  enabled?: boolean
  mapSetFields?: SetFieldsMapper
  /** How this document stores the identity operations target; defaults to `id`. */
  identityPath?: string[]
}): T {
  const pathKey = connectionPathKey(connectionPath)
  const targets = useMemo<ConnectionMutationTarget[]>(
    () => [{ownerId, connectionPath: JSON.parse(pathKey) as string[]}],
    [ownerId, pathKey],
  )
  return useMergedConnections({base, targets, enabled, mapSetFields, identityPath})
}

/** Overlays pending operations for multiple exact owner/path targets onto one denormalized
 * document. Useful for lists that contain many independently mutable owners. */
export function useMergedConnections<T>({
  base,
  targets,
  enabled = true,
  mapSetFields,
  identityPath,
}: {
  base: T
  targets: ConnectionMutationTarget[]
  enabled?: boolean
  mapSetFields?: SetFieldsMapper
  /**
   * How this document stores the identity operations target; defaults to `id`. A producer
   * publishes the entity's global id alone, so a denormalized document that selects it as
   * `globalId` declares that here rather than every producer having to know this shape.
   */
  identityPath?: string[]
}): T {
  const pendingEntries = useMutationState<PendingEntry | undefined>({
    // Subscribe to the broad `['connection']` prefix, not this consumer's exact key:
    // `useMutationState` only recomputes on a mutation-cache notification, not when its
    // filter options change, so a key-scoped subscription would momentarily drop the newly
    // selected connection's already-pending operations when a mounted consumer switches
    // connections. The synchronous owner+path filter in the memo below narrows to this one.
    filters: {mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'},
    // Carry the owner and path from the mutation's own key, in insertion (dispatch) order —
    // the last-write-wins tiebreaker `applyOperations` needs.
    select: mutation => {
      const operation = (mutation.state.variables as ConnectionMutationVariables | undefined)?.operation
      const key = mutation.options.mutationKey
      if (!operation || !Array.isArray(key) || key[0] !== CONNECTION_MUTATION_KEY_ROOT) return undefined
      return {ownerId: String(key[1]), connectionPath: Array.isArray(key[2]) ? (key[2] as string[]) : [], operation}
    },
  })

  // Arrays are new each render; compare by a stable, collision-free key, as with connection paths.
  const identityKey = identityPath ? connectionPathKey(identityPath) : undefined

  return useMemo(() => {
    if (!enabled) return base
    const targetKeys = new Set(targets.map(target => connectionTargetKey(target.ownerId, target.connectionPath)))
    const operations = pendingEntries.flatMap(entry => {
      if (entry == null || !targetKeys.has(connectionTargetKey(entry.ownerId, entry.connectionPath))) return []
      const operation =
        entry.operation.type === 'setFields' && mapSetFields
          ? (() => {
              const fields = mapSetFields(entry.operation.fields)
              return fields ? {...entry.operation, fields} : undefined
            })()
          : entry.operation
      return operation ? [{...entry, operation}] : []
    })
    if (operations.length === 0) return base
    return applyOperations(base, operations, identityKey ? {identityPath: JSON.parse(identityKey) as string[]} : {})
    // `pendingEntries` keeps a stable reference across renders (via `replaceEqualDeep`), so
    // it's a sound dependency directly — no hashing needed.
  }, [enabled, base, targets, pendingEntries, mapSetFields, identityKey])
}
